define((require) => {
    "use strict";

    const _                  = require("underscore");
    const MathUtil           = require("shared/util/MathUtil").default;
    const Marionette         = require("marionette");
    const WidgetTypes        = require("shared/ui/experience/widgets/WidgetTypes");
    const FreeTransform      = require("ui/experience/widgets/transformations/FreeTransform");
    const Preconditions      = require("shared/util/Preconditions");
    const TransformerManager = require("ui/editor/TransformerManager");

    return Marionette.Controller.extend({

        _freeTransform: null,

        _viewModel: null,

        _borderClass: {
            dynamicProductWidget: "dynamic-product-widget-border"
        },

        initialize({layer, scene, viewModel}) {
            Preconditions.checkArgument(layer);
            Preconditions.checkArgument(scene);
            Preconditions.checkArgument(viewModel);

            this._viewModel = viewModel;

            this._freeTransform = new FreeTransform({
                layer,
                scene,
                viewModel
            });

            TransformerManager.add(this._freeTransform);
            this._addListeners();
        },

        setViewportBoundingBoxProvider(viewportBoundingBoxProviderFunction) {
            this._freeTransform.setBoundingBoxProvider(viewportBoundingBoxProviderFunction);
        },

        translate(x, y) {
            this._freeTransform.translate(x, y);
        },

        _addListeners() {
            this.listenTo(this._freeTransform, FreeTransform.TARGET_TRANSFORMED, this._updateWidgetModel);
            this.listenTo(this._freeTransform, "all", this._delegateEvent);
            this.listenTo(this._freeTransform, FreeTransform.MOUSEDOWN, this._setWidgetsSelection);
        },

        /**
         * Set the selected widgets from the targets, targets can be empty array
         * @param {TElement[]} targets  array of targets
         * @private
         */
        _setWidgetsSelection(targets) {
            const widgets = _.pluck(_.pluck(targets, "data"), "widget");
            this.trigger("setSelectedWidgets", widgets);
        },

        /**
         * Trigger events on widgets that are passed from FreeTransform
         * @param {String} eventName    An event name passed in
         * @param {TElement} target     The target passed from Freetransform, the widget can be accessed from the data attribute
         * @private
         */
        _delegateEvents(eventName, target) {
            const widget = target[0].data && target[0].data.widget;
            if (widget) {
                widget.trigger(eventName);
            }
        },

        // Destroy free transform when the scene is closed
        // We only remove the reference to the freeTransform and let it be collected by GC
        removeTransformer() {
            TransformerManager.remove(this._freeTransform);
        },

        editorRenderHandler(scale) {
            this._freeTransform.setContainer(this.options.container);
            this.updateControls(scale);
        },

        /**
         * Adds a widget to FreeTransform so you can interact with it
         *
         * @param itemView
         */
        addWidget(itemView, parentTransform = null) {
            const type = itemView.model.get("type");

            this._freeTransform.add(itemView.el, {
                controls: WidgetTypes.lookup(type).freeTransformConfig,
                widget: itemView,
                parentTransform: parentTransform
            });
        },

        removeWidget(itemView) {
            this._freeTransform.remove(itemView.el);
        },

        /**
         * Updates FreeTransform controls
         */
        updateControls(scale) {
            this._freeTransform.updateControls(scale);
            let selection = this.options.viewModel.selection;

            // Change the control/border color if the widget is a dynamic-product
            if (selection && selection.get("widgets") && this._isDynamicProductWidgetSelected(selection.get("widgets"))) {
                this._freeTransform.setClass(this._borderClass.dynamicProductWidget);
            } else {
                this._freeTransform.removeClass(this._borderClass.dynamicProductWidget);
            }
        },

        /**
         * Hides FreeTransform controls
         */
        hideControls() {
            this._freeTransform.hideControls();
        },

        /**
         * Selects a widget (updating controls, etc)
         */
        selectWidget(views) {
            if (views.length) {
                this._freeTransform.select(_.pluck(views, "el"));
                // Change the border color of the controls if the widget is a dynamic-product
                if (this._isDynamicProductWidgetSelected(views)) {
                    this._freeTransform.setClass(this._borderClass.dynamicProductWidget);
                } else {
                    this._freeTransform.removeClass(this._borderClass.dynamicProductWidget);
                }
            } else {
                this._freeTransform.deselect();
                this.hideControls();
            }
        },

        _updateWidgetModel(targets) {
            targets.forEach((target) => {
                this._updateWidgetFromMatrix(target.data.widget, target.getMatrix());
            });
        },

        _updateWidgetFromMatrix(widget, matrix) {
            const scaledSize = widget.model.getScaledSize(matrix.getScaleX(), matrix.getScaleY());
            const experience = widget.viewModel.get("experience");
            let normalizedScaledSize = {};
            // Round dimensions and positioning to contain exactly required for
            // experience size values accuracy. Uses here for the changes has been made
            // widgets transformations
            if (scaledSize.width) {
                normalizedScaledSize.width = MathUtil.roundToContain(scaledSize.width, experience.get("width"));
            }
            if (scaledSize.height) {
                normalizedScaledSize.height = MathUtil.roundToContain(scaledSize.height, experience.get("height"));
            }
            const experienceSize = this._viewModel.get("experience").getSize();
            const properties = {
                left: MathUtil.roundToContain((matrix.x / experienceSize.width) * 100, experience.get("width")),
                top: MathUtil.roundToContain((matrix.y / experienceSize.height) * 100, experience.get("height")),
                rotation: matrix.getRotation()
            };

            _.extend(properties, normalizedScaledSize);
            widget.model.set(properties, {render: true});
            widget.model.trigger("transformed");
        },

        _delegateEvent() {
            this.trigger.apply(this, arguments);
        },

        _isDynamicProductWidgetSelected(widgets) {
            return widgets.length === 1
                && widgets[0].model.get("type") === WidgetTypes.DYNAMIC_PRODUCT.id;
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/editor/WidgetTransformer.js